/**
 * The body as the UI draws it — shared by the HUD's corner silhouette and the
 * Health tab, so the big one is a strict enlargement of the small one.
 */

/** One limb's rectangle in the silhouette's natural box. */
export interface LimbRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** The silhouette's natural size. Scale the rects by `k` to draw it at `k×`. */
export const LIMB_BOX_W = 64;
export const LIMB_BOX_H = 92;

/**
 * Positions in `LIMB_IDS` order. Arms on the OUTSIDE, left arm on the left — a
 * view from behind, matching `hitbox.ts` where `armL` sits at negative x.
 */
export const LIMB_LAYOUT: readonly LimbRect[] = [
  { left: 22, top: 0, width: 20, height: 18 }, // head
  { left: 18, top: 21, width: 28, height: 24 }, // thorax
  { left: 20, top: 48, width: 24, height: 16 }, // stomach
  { left: 2, top: 22, width: 13, height: 34 }, // armL
  { left: 49, top: 22, width: 13, height: 34 }, // armR
  { left: 20, top: 67, width: 11, height: 25 }, // legL
  { left: 33, top: 67, width: 11, height: 25 }, // legR
];

/** Short English names, in `LIMB_IDS` order. */
export const LIMB_LABELS: readonly string[] = [
  "Head",
  "Thorax",
  "Stomach",
  "Left arm",
  "Right arm",
  "Left leg",
  "Right leg",
];

/**
 * Green → yellow → red by hp fraction, BLACK at zero — a blacked limb is a
 * categorically different state, not a darker red.
 */
export function limbColor(frac: number): string {
  if (!(frac > 0)) return "#141414";
  const f = frac > 1 ? 1 : frac;
  // Hue 0 (red) → 120 (green). Squared so the drop into red happens early,
  // which is when a limb actually starts to matter.
  const hue = 120 * f * f;
  return `hsl(${hue.toFixed(0)}, 62%, ${(38 + 10 * f).toFixed(0)}%)`;
}
